// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  CircularProgress,
  Button,
  Pagination,
  Box,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { omitBy } from 'lodash';
import FileDownload from 'js-file-download';
import SearchIcon from '@mui/icons-material/Search';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import { UserListHead } from '../sections/@dashboard/user';
import { Search, SearchIconWrapper, StyledInputBase } from '../components/SearchInput';

// store
import { api as stockApi } from '../store/domains/stock';

const TABLE_HEAD = [
  { id: 'created_at', label: 'Transaction date ', alignRight: false },
  { id: 'MSISDN', label: 'MSISDN', alignRight: false },
  { id: 'ICCID', label: 'ICCID', alignRight: false },
  { id: 'customerType', label: 'Customer type', alignRight: false },
  { id: 'pin', label: 'Fin code', alignRight: false },
  { id: 'recognitionType', label: 'Recognition Type', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'Reason', label: 'Reason', alignRight: false },
  { id: 'Contract', label: 'Contract', alignRight: false },
  { id: 'files', label: 'Files', alignRight: false },
  { id: 'Action', label: 'Action', alignRight: false },
];

const MySwal = withReactContent(Swal);

export default function TransferOwnership() {
  const [page, setPage] = useState(1);
  const [search, setSearchValue] = useState('');
  const [prefix, setPrefixSearchValue] = useState('');
  const params = useMemo(
    () => omitBy({ msisdn: search, page, prefix }, (v) => v.toString().length < 1),
    [search, page, prefix]
  );
  const { data, isLoading } = stockApi.useGetTransferOwnershipHistory(params);
  const [downloadContract, { isLoading: contractLoading }] = stockApi.useGetDownloadContract();

  const [downloadContractFiles, { isLoading: downloadLoading }] = stockApi.useGetTransferOwnershipFiles();

  const [changeStatus] = stockApi.useChangeTransferOwnershipStatus();

  const handleDownloadFiles = async (values) => {
    const { pin, trackId, providerId, msisdn } = values;
    const response = await downloadContractFiles({
      pin,
      'track-id': trackId,
      providerId,
    }).unwrap();
    console.log(response);
    FileDownload(response, `${msisdn}.zip`);
  };

  const handleDownloadContract = async (values, pinForContract) => {
    const { trackId, contractToken, providerId, pin, msisdn: number } = values;
    const params = {
      trackId,
      token: contractToken,
      pinCode: pinForContract,
      providerId,
    };

    try {
      const response = await downloadContract(params).unwrap();
      console.log(response);
      FileDownload(response, `${number}.pdf`);
    } catch (error) {
      params.pinCode = pin;
      try {
        const response = await downloadContract(params).unwrap();
        FileDownload(response, `${number}.pdf`);
      } catch (error) {
        MySwal.fire({
          title: <p>{error}</p>,
          icon: 'error',
        });
      }
    }
  };

  const handleChangeStatus = async (transaction, type) => {
    if (type === 'APPROVED') {
      const { trackId, customerType } = transaction;
      const response = await changeStatus({
        trackId,
        customerType,
        reason: 'APPROVED',
        status: 'APPROVED',
      }).unwrap();
      if (response) {
        MySwal.fire({
          title: 'Success',
          icon: 'success',
        });
      }
    } else {
      MySwal.fire({
        title: 'Are you sure?',
        text: 'Do you want to change status?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        input: 'text',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const { trackId, customerType } = transaction;
          const response = await changeStatus({
            trackId,
            customerType,
            reason: result.value,
            status: 'REJECTED',
          }).unwrap();
          if (response) {
            MySwal.fire({
              title: 'Success',
              icon: 'success',
            });
          }
        }
      });
    }
  };
  const handleChangeSearchInput = (event) => {
    setSearchValue(event.target.value);
  };
  const handleChangePrefixSearchInput = (event) => {
    setPrefixSearchValue(event.target.value);
  };

  function getStatusChipColor(status) {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'APPROVED':
        return 'success';
      default:
        return 'primary';
    }
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Page title="User">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Stack spacing={3} sx={{ my: 2 }}>
          <Box display="flex" spacing={3} sx={{ my: 2 }}>
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                onChange={handleChangePrefixSearchInput}
                value={prefix}
                placeholder="Prefix"
                inputProps={{ 'aria-label': 'search' }}
              />
            </Search>
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                onChange={handleChangeSearchInput}
                value={search}
                placeholder="Msisdn"
                inputProps={{ 'aria-label': 'search' }}
              />{' '}
            </Search>
          </Box>
        </Stack>
      </Stack>
      <Card>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead headLabel={TABLE_HEAD} rowCount={data?.data?.length} />
              <TableBody>
                {data?.data?.map((row, index) => (
                  <TableRow hover key={index} tabIndex={-1}>
                    <TableCell align="left">{index + 1}</TableCell>
                    <TableCell align="left">{format(new Date(row.salesDate), 'MM.dd.yyyy HH:mm')}</TableCell>
                    <TableCell align="left">{row.msisdn}</TableCell>
                    <TableCell align="left">{row.iccid}</TableCell>
                    <TableCell align="left">{row.customerType}</TableCell>
                    <TableCell align="left">{row.pin}</TableCell>
                    <TableCell align="left">{row.recognitionTypes}</TableCell>
                    <TableCell align="left">
                      <Chip label={row.status} color={getStatusChipColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell align="left">{row.reason}</TableCell>
                    <TableCell align="left">
                      {row.customerType !== 'DONOR' && (
                        <Button
                          sx={{
                            marginLeft: '-1rem',
                          }}
                          variant="contained"
                          onClick={() => handleDownloadContract(row, row.pinForContract)}
                          disabled={contractLoading}
                        >
                          Download
                        </Button>
                      )}
                    </TableCell>{' '}
                    <TableCell align="left">
                      <Button
                        sx={{
                          marginLeft: '-1rem',
                        }}
                        variant="contained"
                        onClick={() => handleDownloadFiles(row, row.pinForContract)}
                        disabled={downloadLoading}
                      >
                        Download
                      </Button>
                    </TableCell>
                    <TableCell align="left">
                      {row.status === 'PENDING' && (
                        <>
                          <Tooltip title="Reject">
                            <IconButton onClick={() => handleChangeStatus(row, 'REJECTED')}>
                              <CancelOutlinedIcon color="error" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <IconButton onClick={() => handleChangeStatus(row, 'APPROVED')}>
                              <CheckOutlinedIcon color="success" />
                            </IconButton>
                          </Tooltip>{' '}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
      <Stack padding={2} spacing={2} justifyContent="center">
        {data?.metadata?.pagination?.total_page > 1 && (
          <Pagination
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
            count={data?.metadata?.pagination?.total_page}
            color="primary"
            onChange={(_, val) => setPage(val)}
          />
        )}
      </Stack>
    </Page>
  );
}
